import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiExternalLink, FiArrowRight } = FiIcons;

const Footer = () => {
  const navigate = useNavigate();

  const handleInternalLink = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="mt-20 border-t border-white/5 bg-[#09090b] pt-20 pb-10 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-20">
          {/* Column 1: Brand & Address */}
          <div className="space-y-6">
            <button onClick={() => handleInternalLink('/')} className="inline-block group bg-transparent border-none outline-none cursor-pointer">
              <h2 className="text-3xl font-black text-white tracking-tight leading-none">
                Video<span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-500 to-orange-600">Examples</span>
              </h2>
            </button>
            <div className="flex items-start gap-4 text-slate-400 group">
              <div className="p-2.5 rounded-xl bg-white/5 text-orange-500/60 mt-1 group-hover:bg-orange-500/10 group-hover:text-orange-500 transition-all">
                <SafeIcon icon={FiMapPin} />
              </div>
              <div>
                <p className="text-sm leading-relaxed font-medium">
                  758 Lakeview Dr <br />
                  Wheeling, IL, 60090
                </p>
              </div>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="space-y-6">
            <h4 className="text-white text-xs font-black uppercase tracking-[0.25em]">Navigation</h4>
            <ul className="space-y-4">
              <FooterLink to="/" label="Video Collection" onNavigate={handleInternalLink} />
              <FooterLink to="/directory" label="Compare Video Companies" onNavigate={handleInternalLink} />
              <li>
                <a
                  href="https://www.whatastory.agency/resources/ai-quote?source=videoexamples.com&button=footer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="group flex items-center gap-2 text-slate-400 hover:text-orange-500 transition-all text-sm font-bold">
                    Request a Quote
                    <SafeIcon icon={FiExternalLink} className="text-[10px] opacity-0 group-hover:opacity-100 transition-all" />
                  </span>
                </a>
              </li>
              <FooterLink to="/truth" label="The Truth" onNavigate={handleInternalLink} />
              <li>
                <button onClick={() => handleInternalLink('/winners')} className="group flex items-center gap-2 bg-transparent border-none outline-none cursor-pointer transition-all text-sm font-black">
                  <span className="bg-gradient-to-r from-orange-400 via-amber-500 to-orange-600 bg-clip-text text-transparent group-hover:scale-105 transition-transform inline-block">
                    Monthly Winners
                  </span>
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: Mission + Privacy */}
          <div className="lg:col-span-2 space-y-6">
            <h4 className="text-white text-xs font-black uppercase tracking-[0.25em]">Curator's Note</h4>
            <p className="text-slate-500 text-sm leading-relaxed max-w-md italic">
              "We help visionary B2B companies find the perfect video production agency for their goals—with zero sales pressure. Explore our curated gallery to understand exactly why a video succeeded, its estimated cost, and its potential ROI."
            </p>
            <button
              onClick={() => handleInternalLink('/privacy')}
              className="text-slate-600 hover:text-slate-400 text-xs transition-colors underline underline-offset-2"
            >
              Privacy Policy
            </button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-slate-600 text-[10px] font-black uppercase tracking-widest">
            © {new Date().getFullYear()} VIDEO EXAMPLES. ALL RIGHTS RESERVED.
          </div>
          <div className="text-slate-700 text-[10px] italic tracking-wide">
            Curated by the SaaS Specialists at What a Story
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterLink = ({ to, label, onNavigate, icon }) => {
  return (
    <li>
      <button
        onClick={() => onNavigate(to)}
        className="group flex items-center gap-2 text-slate-400 hover:text-orange-500 transition-all text-sm font-bold bg-transparent border-none outline-none cursor-pointer"
      >
        {icon && <SafeIcon icon={icon} className="text-xs" />}
        {label}
        <SafeIcon icon={FiArrowRight} className="text-[10px] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
      </button>
    </li>
  );
};

export default Footer;